import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Check,
  Eye,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Trash2,
  User,
} from 'lucide-react';
import { useParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import { type GetLeadAgentStatusQuery } from '@graphql/__generated__/graphql';
import { iconButtonSx } from '@modules/_shared/components/CardHeaderIconActions';
import { CopyableText } from '@modules/_shared/components/CopyableText';
import { CreateListingModal } from '@modules/_shared/components/create-listing/CreateListingModal';
import { SelectField } from '@modules/_shared/components/form/RaSelect';
import { LeadUpdatedTimeAgo } from '@modules/_shared/components/lead/LeadUpdatedTimeAgo';
import { LeadLostButton } from '@modules/_shared/components/LeadLostButton';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { UserSelect } from '@modules/_shared/components/user-select/UserSelect';
import { useUpdateLeadAgents } from '@modules/_shared/hooks/useUpdateLeadAgents';
import { getContactDisplayNames } from '@modules/_shared/utils/contact';
import {
  formatAddress,
  formatPhone,
  formatPostcodeLocality,
  getLeadLocation,
} from '@modules/_shared/utils/formatting';
import { getLeadQualificationSummary } from '@modules/_shared/utils/lead-labels';
import {
  createLeadStageRenderOption,
  createLeadStageRenderValue,
  getStageBackgroundColor,
  selectInputStyles,
} from '@modules/_shared/utils/leadStageSelectHelpers';
import { ClaimToReveal } from '@modules/lead-details/ClaimToReveal';
import { useLeadPermissions } from '@modules/lead-details/hooks/useLeadPermissions';
import { GET_LEAD_DETAILS } from '@modules/lead-details/LeadDetailsDrawer';
import { GET_BROKERAGE_PIPELINE, GET_LEADS } from '@modules/leads/leadsQueries';
import { useLocale } from '@shared/hooks/locale';

type LeadContactInfoProps = {
  address: string;
  qualification: string;
  phoneNumber: string | undefined;
  email: string | undefined;
  canViewContactDetails: boolean;
  postcode?: string | null;
  locality?: string | null;
  state?: string | null;
};

const LeadContactInfo = ({
  address,
  qualification,
  phoneNumber,
  email,
  canViewContactDetails,
  postcode,
  locality,
  state,
}: LeadContactInfoProps) => {
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box
          sx={{
            color: 'text.secondary',
            flexShrink: 0,
            display: 'flex',
            mt: '3px',
          }}
        >
          <MapPin size={16} />
        </Box>
        {canViewContactDetails ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {address}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {[formatPostcodeLocality(postcode, locality), state]
              .filter(Boolean)
              .join(', ')}
          </Typography>
        )}
      </Box>
      {qualification && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ color: 'text.secondary', flexShrink: 0, display: 'flex' }}>
            <User size={16} />
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {qualification}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color: 'text.secondary', flexShrink: 0, display: 'flex' }}>
          <Phone size={16} />
        </Box>
        {canViewContactDetails ? (
          phoneNumber ? (
            <CopyableText text={phoneNumber}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {phoneNumber}
              </Typography>
            </CopyableText>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              -
            </Typography>
          )
        ) : (
          <ClaimToReveal />
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color: 'text.secondary', flexShrink: 0, display: 'flex' }}>
          <Mail size={16} />
        </Box>
        {canViewContactDetails ? (
          email ? (
            <CopyableText text={email}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {email}
              </Typography>
            </CopyableText>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              -
            </Typography>
          )
        ) : (
          <ClaimToReveal />
        )}
      </Box>
    </>
  );
};

type LeadAgent = GetLeadAgentStatusQuery['lead_agents'][0];

type LeadAgentStageSelectorProps = {
  leadId: string;
  leadAgents: LeadAgent[];
  requiresAgentSelection: boolean;
  canViewContactDetails: boolean;
  hasListing: boolean;
  onWonStageRequested: (stageId: string, agentId: string) => void;
  onError: (error: ErrorLike) => void;
  selectedAgentId?: string | null;
  setSelectedAgentId: (agentId: string | null) => void;
};

const LeadAgentStageSelector = ({
  leadId,
  leadAgents,
  requiresAgentSelection,
  canViewContactDetails,
  hasListing,
  onWonStageRequested,
  onError,
  selectedAgentId,
  setSelectedAgentId,
}: LeadAgentStageSelectorProps) => {
  const { t } = useLocale();
  const [localStageId, setLocalStageId] = useState<string>('');

  useEffect(() => {
    if (
      selectedAgentId == null &&
      ((leadAgents.length > 0 && requiresAgentSelection === false) ||
        leadAgents.length === 1)
    ) {
      setSelectedAgentId(leadAgents[0].user.id);
    }
  }, [leadAgents, selectedAgentId, setSelectedAgentId, requiresAgentSelection]);

  const selectedLeadAgent = useMemo(() => {
    return leadAgents.find(agent => agent.user.id === selectedAgentId);
  }, [leadAgents, selectedAgentId]);

  const currentStage = selectedLeadAgent?.lead_stage;
  const pipelineId = currentStage?.pipeline?.id;

  // Fetch available stages for the agent's pipeline
  const { data: stagesData } = useQuery(GET_BROKERAGE_PIPELINE, {});

  const [updateLeadAgents, { loading: updateLeadAgentsLoading }] =
    useUpdateLeadAgents({
      onError,
      awaitRefetchQueries: true,
      refetchQueries: [GET_LEADS],
    });

  const stages = useMemo(
    () => stagesData?.pipelines[0]?.stages ?? [],
    [stagesData],
  );

  useEffect(() => {
    const currentStageId = currentStage?.id ?? '';
    if (localStageId !== currentStageId) {
      setLocalStageId(currentStageId);
    }
  }, [currentStage?.id, localStageId]);

  const handleStageChange = useCallback(
    async (stageId: string) => {
      if (!selectedLeadAgent?.user?.id) {
        return;
      }

      const selectedStage = stages.find(stage => stage.id === stageId);

      if (selectedStage?.status === 'won' && !hasListing) {
        onWonStageRequested(stageId, selectedLeadAgent.user.id);
        return;
      }

      setLocalStageId(stageId);
      await updateLeadAgents(
        leadId,
        { stage_id: stageId },
        selectedLeadAgent.user.id,
      );
    },
    [
      selectedLeadAgent?.user.id,
      stages,
      updateLeadAgents,
      leadId,
      hasListing,
      onWonStageRequested,
    ],
  );

  const handleWonClick = async () => {
    const wonStage = stages.find(stage => stage.status === 'won');

    if (wonStage && selectedLeadAgent?.user?.id) {
      if (!hasListing) {
        onWonStageRequested(wonStage.id, selectedLeadAgent.user.id);
      } else {
        await updateLeadAgents(
          leadId,
          { stage_id: wonStage.id },
          selectedLeadAgent.user.id,
        );
        setLocalStageId(wonStage.id);
      }
    }
  };

  const agentOptions = useMemo(
    () =>
      leadAgents.map(agent => ({
        value: agent.user.id,
        label: `${agent.user.first_name ?? ''} ${
          agent.user.last_name ?? ''
        }`.trim(),
      })),
    [leadAgents],
  );

  const stageOptions = useMemo(
    () =>
      stages.map(stage => ({
        value: stage.id,
        label: stage.label ?? '',
      })),
    [stages],
  );

  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const stageBackgroundColor = useMemo(
    () => getStageBackgroundColor(currentStage?.status, palette),
    [currentStage?.status, palette],
  );

  if (!requiresAgentSelection && !selectedLeadAgent) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {requiresAgentSelection && (
        <Box sx={{ '& .MuiOutlinedInput-root': selectInputStyles }}>
          <SelectField
            field={{
              value: selectedAgentId ?? '',
              onChange: e => setSelectedAgentId(e.target.value as string),
              onBlur: () => {},
              name: 'agent_id',
              ref: () => {},
            }}
            options={agentOptions}
            placeholder={t('Select an agent to view the status')}
          />
        </Box>
      )}
      {selectedLeadAgent && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row',
              md: 'column',
              lg: 'row',
            },
            gap: 1,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              '& .MuiOutlinedInput-root': {
                ...selectInputStyles,
                background: stageBackgroundColor,
              },
            }}
          >
            <SelectField
              field={{
                value: localStageId,
                onChange: e => handleStageChange(e.target.value as string),
                onBlur: () => {},
                name: 'stage_id',
                ref: () => {},
              }}
              options={stageOptions}
              disabled={!canViewContactDetails}
              renderValue={createLeadStageRenderValue(stages)}
              renderOption={createLeadStageRenderOption(stages)}
              loading={updateLeadAgentsLoading}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <LeadLostButton
              leadId={leadId}
              pipelineId={pipelineId}
              agentId={selectedLeadAgent?.user?.id ?? null}
              currentStageId={localStageId}
              disabled={
                !canViewContactDetails ||
                selectedLeadAgent == null ||
                !stages.some(stage => stage.status === 'lost') ||
                currentStage?.status === 'won' ||
                currentStage?.status === 'lost'
              }
              onStageUpdated={stageId => setLocalStageId(stageId)}
            />
            <Button
              variant="soft"
              color="success"
              startIcon={<Check />}
              onClick={handleWonClick}
              disabled={
                !canViewContactDetails ||
                !stages.some(stage => stage.status === 'won') ||
                currentStage?.status === 'won' ||
                currentStage?.status === 'lost'
              }
            >
              {t('Won')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

type LeadDetailsStatusCardProps = {
  leadId?: string;
  disabled?: boolean;
  canViewContactDetails?: boolean;
  onContactUpdated?: () => Promise<void> | void;
  selectedAgentId?: string | null;
  setSelectedAgentId: (agentId: string | null) => void;
  requiresAgentSelection?: boolean;
};

export const GET_LEAD_AGENT_STATUS = gql(/* GraphQL */ `
  query GetLeadAgentStatus($lead_id: uuid!) {
    lead_agents(
      where: { lead_id: { _eq: $lead_id }, passed_at: { _is_null: true } }
    ) {
      id
      lead_stage {
        id
        label
        status
        pipeline {
          id
        }
      }
      user {
        id
        first_name
        last_name
      }
    }
  }
`);

export const UPDATE_LEAD_CONTACT = gql(/* GraphQL */ `
  mutation UpdateLeadContact($leadId: uuid!, $contactId: uuid) {
    update_leads_by_pk(
      pk_columns: { id: $leadId }
      _set: { contact_id: $contactId }
    ) {
      id
      contact_id
    }
  }
`);

export const LeadDetailsStatusCard = ({
  leadId: leadIdProp,
  canViewContactDetails = true,
  onContactUpdated,
  selectedAgentId,
  setSelectedAgentId,
  requiresAgentSelection,
}: LeadDetailsStatusCardProps) => {
  const { t } = useLocale();
  const params = useParams<{ leadId?: string }>();
  const leadId = leadIdProp ?? params.leadId ?? '';

  const [isCreateListingModalOpen, setIsCreateListingModalOpen] =
    useState(false);
  const [pendingWonAgentId, setPendingWonAgentId] = useState<string | null>(
    null,
  );

  const [isEditingContact, setIsEditingContact] = useState(false);
  const [editAnchorEl, setEditAnchorEl] = useState<HTMLElement | null>(null);
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const [updateLeadContact, { loading: isUpdatingContact }] = useMutation(
    UPDATE_LEAD_CONTACT,
    {
      refetchQueries: [GET_LEAD_DETAILS],
      awaitRefetchQueries: true,
      onError: setErrorLike,
    },
  );

  const handleCreateListingModalClose = () => {
    setIsCreateListingModalOpen(false);
    setPendingWonAgentId(null);
  };

  const { data: leadDetailsData } = useQuery(GET_LEAD_DETAILS, {
    variables: { lead_id: leadId },
    skip: !leadId,
  });
  const lead = leadDetailsData?.leads_by_pk ?? null;

  const { canManageLead, isAdmin, restrictedAgentView } = useLeadPermissions({
    lead,
  });

  const { data: leadAgentData } = useQuery(GET_LEAD_AGENT_STATUS, {
    variables: { lead_id: leadId },
    skip: !leadId,
  });

  const leadAgents = useMemo(
    () => leadAgentData?.lead_agents ?? [],
    [leadAgentData],
  );

  const handleWonStageRequested = (_stageId: string, agentId: string) => {
    setPendingWonAgentId(agentId);
    setIsCreateListingModalOpen(true);
  };

  // Contact information display
  const contact = lead?.contact ?? null;
  const contactDisplayNames = getContactDisplayNames(
    lead,
    canViewContactDetails,
  );
  const originEnquiryDisplayName = [
    lead?.origin_enquiry?.first_name,
    lead?.origin_enquiry?.last_name,
  ]
    .filter(Boolean)
    .join(' ');
  const displayName =
    contact == null && originEnquiryDisplayName
      ? originEnquiryDisplayName
      : contactDisplayNames?.displayName;
  const profileUrl =
    contact?.id && canViewContactDetails
      ? `/users/${contact.id}/overview`
      : null;

  // Property and qualification
  const property = lead?.property;
  const { postcode, locality } = getLeadLocation(lead);
  const baseAddress = property ? formatAddress(property, ', ', true) : '-';

  const address = property?.state
    ? `${baseAddress}, ${property.state}`
    : baseAddress;

  const qualification = getLeadQualificationSummary(t, lead ?? {});

  // Contact details
  const rawPhoneNumber =
    contact?.phone_numbers?.[0]?.number ||
    lead?.origin_enquiry?.phone_number ||
    lead?.phone_number ||
    '';
  const phoneNumber = formatPhone(rawPhoneNumber) ?? rawPhoneNumber;
  const email =
    contact?.emails?.[0]?.email ||
    lead?.origin_enquiry?.email_address ||
    lead?.email_address ||
    '';

  // Permissions
  const canEditContact = canManageLead;
  const showEditButton = Boolean(
    (lead?.origin_enquiry_id == null || isAdmin) &&
      !restrictedAgentView &&
      canEditContact,
  );

  useEffect(() => {
    setIsEditingContact(false);
    setEditAnchorEl(null);
  }, [leadId]);

  return (
    <>
      <ListingAccordion defaultExpanded disableCollapse>
        <Stack
          sx={{
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flex: '0 0 75%',
              }}
            >
              {isEditingContact && showEditButton ? (
                <ClickAwayListener
                  onClickAway={() => setIsEditingContact(false)}
                >
                  <Box sx={{ width: '100%', minWidth: 200 }}>
                    <UserSelect
                      userId={contact?.id}
                      autoFocus
                      onChange={async userId => {
                        if (userId) {
                          await updateLeadContact({
                            variables: { leadId, contactId: userId },
                          });
                          await onContactUpdated?.();
                          setIsEditingContact(false);
                        }
                      }}
                    />
                  </Box>
                </ClickAwayListener>
              ) : (
                <>
                  {profileUrl ? (
                    <Link
                      href={profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        textDecoration: 'none',
                        color: 'inherit',

                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {displayName}
                    </Link>
                  ) : (
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {displayName}
                    </Typography>
                  )}
                  {showEditButton && (
                    <IconButton
                      size="small"
                      onClick={event => {
                        setEditAnchorEl(event.currentTarget);
                      }}
                      sx={iconButtonSx}
                    >
                      <Pencil size={16} />
                    </IconButton>
                  )}
                </>
              )}
            </Box>

            {lead && (
              <Typography
                variant="caption"
                component="div"
                sx={{
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                <LeadUpdatedTimeAgo lead={lead} showBothDate={isAdmin} />
              </Typography>
            )}
          </Box>

          <LeadContactInfo
            address={address}
            qualification={qualification}
            phoneNumber={phoneNumber}
            email={email}
            canViewContactDetails={canViewContactDetails}
            postcode={postcode}
            locality={locality}
            state={property?.state}
          />

          {leadAgents.length > 0 && (
            <LeadAgentStageSelector
              leadId={leadId}
              leadAgents={leadAgents}
              requiresAgentSelection={!!requiresAgentSelection}
              canViewContactDetails={canViewContactDetails}
              hasListing={(lead?.lots?.length ?? 0) > 0}
              onWonStageRequested={handleWonStageRequested}
              onError={setErrorLike}
              selectedAgentId={selectedAgentId}
              setSelectedAgentId={setSelectedAgentId}
            />
          )}
        </Stack>
      </ListingAccordion>
      <Menu
        open={Boolean(editAnchorEl) && showEditButton}
        anchorEl={editAnchorEl}
        onClose={() => setEditAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {profileUrl && (
          <MenuItem
            component="a"
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setEditAnchorEl(null)}
          >
            <Box sx={{ mr: 1, display: 'flex' }}>
              <Eye size={16} />
            </Box>
            {t('View profile')}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setIsEditingContact(true);
            setEditAnchorEl(null);
          }}
        >
          <Box sx={{ mr: 1, display: 'flex' }}>
            <Pencil size={16} />
          </Box>
          {t('Edit')}
        </MenuItem>
        {profileUrl && (
          <MenuItem
            onClick={async () => {
              setEditAnchorEl(null);
              await updateLeadContact({
                variables: { leadId, contactId: null },
              });
              await onContactUpdated?.();
            }}
            disabled={isUpdatingContact}
          >
            <Box sx={{ mr: 1, display: 'flex' }}>
              <Trash2 size={16} />
            </Box>
            {t('remove')}
          </MenuItem>
        )}
      </Menu>
      <CreateListingModal
        title={t('Lead won')}
        fromLeadId={leadId}
        open={isCreateListingModalOpen}
        brokerId={pendingWonAgentId ?? undefined}
        onClose={handleCreateListingModalClose}
      />
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};
